import api, { ApiResponse } from './api';

export interface Paciente {
  id: number;
  nombre: string;
  fechaNacimiento: string;
  tutor?: string;
  direccion?: string;
  telefono?: string;
  estado?: string;
  municipio?: string;
  sexo: string;
  peso?: number;
  estatura?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  consultas?: Consulta[];
  _count?: {
    consultas: number;
  };
}

export interface Consulta {
  id: number;
  pacienteId: number;
  fecha: string;
  temperatura?: number;
  peso?: number;
  estatura?: number;
  revisionFisica?: string;
  observaciones?: string;
  createdAt: string;
  updatedAt: string;
  paciente?: Paciente;
  diagnosticos?: Diagnostico[];
  medicamentos?: Medicamento[];
}

export interface Diagnostico {
  id: number;
  consultaId: number;
  diagnostico: string;
  createdAt: string;
}

export interface Medicamento {
  id: number;
  consultaId: number;
  medicamento: string;
  dosis: string;
  frecuencia: string;
  duracion: string;
  indicaciones?: string;
  createdAt: string;
}

export interface CreatePacienteDto {
  nombre: string;
  fechaNacimiento: string;
  tutor?: string;
  direccion?: string;
  telefono?: string;
  estado?: string;
  municipio?: string;
  sexo: string;
  peso?: number;
  estatura?: number;
}

export interface UpdatePacienteDto extends Partial<CreatePacienteDto> {
  isActive?: boolean;
}

export interface CreateConsultaDto {
  pacienteId: number;
  fecha?: string;
  temperatura?: number;
  peso?: number;
  estatura?: number;
  revisionFisica?: string;
  observaciones?: string;
  diagnosticos?: { diagnostico: string }[];
  medicamentos?: {
    medicamento: string;
    dosis: string;
    frecuencia: string;
    duracion: string;
    indicaciones?: string;
  }[];
}

export interface UpdateConsultaDto extends Partial<Omit<CreateConsultaDto, 'pacienteId'>> {}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface RecetaLayoutPosition {
  formato?: string;
  campo: string;
  top: number;
  left: number;
  width?: number;
}

export interface UpdateRecetaLayoutDto {
  formato: string;
  positions: RecetaLayoutPosition[];
}

class PacientesService {
  // ==================== PACIENTES ====================

  async getPacientes(params?: {
    skip?: number;
    take?: number;
    search?: string;
    isActive?: boolean;
  }): Promise<PaginatedResponse<Paciente>> {
    const response = await api.get<ApiResponse<PaginatedResponse<Paciente>>>('pacientes', {
      params,
    });
    return response.data.data;
  }

  async getPaciente(id: number): Promise<Paciente> {
    const response = await api.get<ApiResponse<Paciente>>(`pacientes/${id}`);
    return response.data.data;
  }

  async createPaciente(data: CreatePacienteDto): Promise<Paciente> {
    const response = await api.post<ApiResponse<Paciente>>('pacientes', data);
    return response.data.data;
  }

  async updatePaciente(id: number, data: UpdatePacienteDto): Promise<Paciente> {
    const response = await api.put<ApiResponse<Paciente>>(`pacientes/${id}`, data);
    return response.data.data;
  }

  async deletePaciente(id: number): Promise<void> {
    await api.delete(`pacientes/${id}`);
  }

  async getHistorialClinico(pacienteId: number): Promise<Paciente> {
    const response = await api.get<ApiResponse<Paciente>>(`pacientes/${pacienteId}/historial`);
    return response.data.data;
  }

  // ==================== CONSULTAS ====================

  async getConsultas(params?: {
    skip?: number;
    take?: number;
    pacienteId?: number;
    fechaInicio?: string;
    fechaFin?: string;
  }): Promise<PaginatedResponse<Consulta>> {
    const response = await api.get<ApiResponse<PaginatedResponse<Consulta>>>('pacientes/consultas/list', {
      params,
    });
    return response.data.data;
  }

  async getConsulta(id: number): Promise<Consulta> {
    const response = await api.get<ApiResponse<Consulta>>(`pacientes/consultas/${id}`);
    return response.data.data;
  }

  async createConsulta(data: CreateConsultaDto): Promise<Consulta> {
    const response = await api.post<ApiResponse<Consulta>>('pacientes/consultas', data);
    return response.data.data;
  }

  async updateConsulta(id: number, data: UpdateConsultaDto): Promise<Consulta> {
    const response = await api.put<ApiResponse<Consulta>>(`pacientes/consultas/${id}`, data);
    return response.data.data;
  }

  async deleteConsulta(id: number): Promise<void> {
    await api.delete(`pacientes/consultas/${id}`);
  }

  async getReceta(consultaId: number): Promise<Consulta> {
    const response = await api.get<ApiResponse<Consulta>>(`pacientes/consultas/${consultaId}/receta`);
    return response.data.data;
  }

  // ==================== RECETA LAYOUT ====================

  async getRecetaLayout(formato?: string): Promise<RecetaLayoutPosition[]> {
    const params = formato ? { formato } : {};
    const response = await api.get<ApiResponse<RecetaLayoutPosition[]>>('pacientes/receta-layout', { params });
    return response.data.data;
  }

  async updateRecetaLayout(data: UpdateRecetaLayoutDto): Promise<{ message: string; updated: number }> {
    const response = await api.patch<ApiResponse<{ message: string; updated: number }>>(
      'pacientes/receta-layout',
      data
    );
    return response.data.data;
  }
}

export default new PacientesService();
